<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const hasLoaded = ref(false);
const loadError = ref('');
const analytics = ref({});
const conversationAnalytics = ref({});
const granularityOptions = [
  {
    value: 'HALF_HOUR',
    labelKey:
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GRANULARITY_HALF_HOUR',
  },
  {
    value: 'DAILY',
    labelKey: 'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GRANULARITY_DAILY',
  },
  {
    value: 'MONTHLY',
    labelKey:
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GRANULARITY_MONTHLY',
  },
];

const filters = ref({
  start: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10),
  end: new Date().toISOString().slice(0, 10),
  granularity: 'DAILY',
});

const formattedAnalytics = computed(() =>
  JSON.stringify(analytics.value, null, 2)
);
const formattedConversationAnalytics = computed(() =>
  JSON.stringify(conversationAnalytics.value, null, 2)
);

const formatPeriodLabel = rawValue => {
  if (!rawValue) return 'Periodo';

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return String(rawValue);

  return parsed.toLocaleDateString();
};

const metricLabelOverrides = {
  conversations_started: 'Conversaciones iniciadas',
  conversations_resolved: 'Conversaciones resueltas',
  conversations_opened: 'Conversaciones abiertas',
  conversations_closed: 'Conversaciones cerradas',
  conversation: 'Conversaciones',
  conversations: 'Conversaciones',
  sent: 'Enviados',
  delivered: 'Entregados',
  read: 'Leídos',
  clicked: 'Clics',
  failed: 'Fallidos',
  cost: 'Costo',
  volume: 'Volumen',
  total: 'Total',
  marketing: 'Marketing',
  utility: 'Utilidad',
  authentication: 'Autenticación',
  template: 'Plantillas',
  templates: 'Plantillas',
  phone_number: 'Número',
  country: 'País',
  category: 'Categoría',
  endpoint: 'Endpoint',
};

const glossaryItems = computed(() => [
  {
    key: 'sent',
    title: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_SENT'),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_SENT_DESCRIPTION'
    ),
  },
  {
    key: 'delivered',
    title: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_DELIVERED'),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_DELIVERED_DESCRIPTION'
    ),
  },
  {
    key: 'read',
    title: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_READ'),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_READ_DESCRIPTION'
    ),
  },
  {
    key: 'conversations',
    title: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_CONVERSATIONS'
    ),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_CONVERSATIONS_DESCRIPTION'
    ),
  },
  {
    key: 'cost',
    title: t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_COST'),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_COST_DESCRIPTION'
    ),
  },
  {
    key: 'granularity',
    title: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_GRANULARITY'
    ),
    description: t(
      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_GRANULARITY_DESCRIPTION'
    ),
  },
]);

const prettifyMetricLabel = path =>
  path
    .split('.')
    .filter(Boolean)
    .filter(
      segment =>
        !['analytics', 'conversation_analytics', 'data_points'].includes(
          segment
        )
    )
    .map(
      segment =>
        metricLabelOverrides[segment] ||
        segment
          .replace(/_/g, ' ')
          .replace(/\b\w/g, letter => letter.toUpperCase())
    )
    .join(' / ');

const aggregateMetrics = value => {
  const entries = new Map();

  const visit = (node, path = []) => {
    if (typeof node === 'number' && Number.isFinite(node)) {
      const key = path.join('.');
      entries.set(key, (entries.get(key) || 0) + node);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(item => visit(item, path));
      return;
    }

    if (node && typeof node === 'object') {
      Object.entries(node).forEach(([key, nestedValue]) => {
        if (['start', 'end', 'timestamp'].includes(key)) {
          return;
        }

        visit(nestedValue, [...path, key]);
      });
    }
  };

  visit(value);

  return [...entries.entries()]
    .map(([path, metricValue]) => ({
      key: path,
      label: prettifyMetricLabel(path),
      value: metricValue,
    }))
    .sort((left, right) => Math.abs(right.value) - Math.abs(left.value));
};

const analyticsSummary = computed(() =>
  aggregateMetrics(analytics.value).slice(0, 8)
);
const conversationSummary = computed(() =>
  aggregateMetrics(conversationAnalytics.value).slice(0, 8)
);
const analyticsHighlights = computed(() => analyticsSummary.value.slice(0, 4));
const conversationHighlights = computed(() =>
  conversationSummary.value.slice(0, 4)
);
const combinedHighlights = computed(() => {
  const analyticsLead = analyticsHighlights.value[0];
  const conversationLead = conversationHighlights.value[0];

  return [
    analyticsLead
      ? {
          key: `analytics-${analyticsLead.key}`,
          title: t(
            'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TOP_CHANNEL_METRIC'
          ),
          label: analyticsLead.label,
          value: analyticsLead.value,
        }
      : null,
    conversationLead
      ? {
          key: `conversation-${conversationLead.key}`,
          title: t(
            'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TOP_CONVERSATION_METRIC'
          ),
          label: conversationLead.label,
          value: conversationLead.value,
        }
      : null,
  ].filter(Boolean);
});

const collectTimeline = value => {
  const rows = new Map();

  const visit = node => {
    if (Array.isArray(node)) {
      const containsTimeWindows = node.every(
        item =>
          item &&
          typeof item === 'object' &&
          (item.start || item.end || item.timestamp)
      );

      if (containsTimeWindows) {
        node.forEach(item => {
          const label = formatPeriodLabel(
            item.start || item.end || item.timestamp
          );
          const current = rows.get(label) || { label };

          Object.entries(item).forEach(([key, metricValue]) => {
            if (
              ['start', 'end', 'timestamp'].includes(key) ||
              typeof metricValue !== 'number' ||
              !Number.isFinite(metricValue)
            ) {
              return;
            }

            current[key] = (current[key] || 0) + metricValue;
          });

          rows.set(label, current);
        });
        return;
      }

      node.forEach(visit);
      return;
    }

    if (node && typeof node === 'object') {
      Object.values(node).forEach(visit);
    }
  };

  visit(value);
  return [...rows.values()];
};

const analyticsTimeline = computed(() => collectTimeline(analytics.value));
const conversationTimeline = computed(() =>
  collectTimeline(conversationAnalytics.value)
);

const totalRowValue = row =>
  Object.entries(row).reduce((sum, [key, metricValue]) => {
    if (key === 'label' || typeof metricValue !== 'number') {
      return sum;
    }

    return sum + metricValue;
  }, 0);

const timelineMaxValue = rows =>
  rows.reduce((maxValue, row) => Math.max(maxValue, totalRowValue(row)), 0);

const analyticsTimelineMax = computed(() =>
  timelineMaxValue(analyticsTimeline.value)
);
const conversationTimelineMax = computed(() =>
  timelineMaxValue(conversationTimeline.value)
);

const buildTimelineInsight = (rows, title, emptyLabel) => {
  if (!rows.length) {
    return {
      title,
      label: emptyLabel,
      value: '—',
    };
  }

  const firstRow = rows[0];
  const lastRow = rows[rows.length - 1];
  const firstValue = totalRowValue(firstRow);
  const lastValue = totalRowValue(lastRow);
  const delta = lastValue - firstValue;
  const direction = delta > 0 ? '+' : '';
  const strongestRow = rows.reduce((bestRow, currentRow) => {
    if (!bestRow) return currentRow;
    return totalRowValue(currentRow) > totalRowValue(bestRow)
      ? currentRow
      : bestRow;
  }, null);

  return {
    title,
    label: strongestRow?.label || emptyLabel,
    value: `${direction}${delta}`,
    strongestValue: totalRowValue(strongestRow || {}),
  };
};

const insightCards = computed(() => [
  buildTimelineInsight(
    analyticsTimeline.value,
    t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CHANNEL_INSIGHT'),
    t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_NO_PERIOD')
  ),
  buildTimelineInsight(
    conversationTimeline.value,
    t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CONVERSATION_INSIGHT'),
    t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_NO_PERIOD')
  ),
]);

const loadAnalytics = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppAnalytics(
      props.inbox.id,
      filters.value
    );
    analytics.value = response.data?.analytics || {};
    conversationAnalytics.value = response.data?.conversation_analytics || {};
    loadError.value = '';
    hasLoaded.value = true;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_LOAD_ERROR');
    useAlert(loadError.value);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadAnalytics();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div
      class="chatmigo-report-filter-card grid gap-4 rounded-xl border border-n-container bg-n-alpha-2 p-4 lg:grid-cols-4"
    >
      <woot-input
        v-model="filters.start"
        type="date"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_START')"
      />
      <woot-input
        v-model="filters.end"
        type="date"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_END')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        <span class="font-medium">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GRANULARITY') }}
        </span>
        <select
          v-model="filters.granularity"
          class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        >
          <option
            v-for="option in granularityOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ $t(option.labelKey) }}
          </option>
        </select>
      </label>
      <div class="flex items-end">
        <Button
          type="button"
          color="blue"
          icon="i-ph-chart-line"
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_REFRESH')"
          :is-loading="isLoading"
          @click="loadAnalytics"
        />
      </div>
    </div>

    <div
      v-if="loadError"
      class="chatmigo-report-filter-card rounded-xl border border-n-container bg-n-alpha-2 px-4 py-6 text-sm text-n-ruby-11"
    >
      {{ loadError }}
    </div>

    <div v-else class="grid gap-4">
      <div
        class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <p class="mb-3 text-sm font-semibold text-n-slate-12">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_GLOSSARY_TITLE')
          }}
        </p>
        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="item in glossaryItems"
            :key="item.key"
            class="chatmigo-report-metric-card p-3"
          >
            <p class="mb-1 text-sm font-semibold text-n-slate-12">
              {{ item.title }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ item.description }}
            </p>
          </div>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <div
          v-for="card in insightCards"
          :key="card.title"
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
            {{ card.title }}
          </p>
          <p class="mb-1 text-2xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ card.label }}
            <template v-if="card.strongestValue !== undefined">
              · {{ card.strongestValue }}
            </template>
          </p>
        </div>
      </div>

      <div v-if="combinedHighlights.length" class="grid gap-4 lg:grid-cols-2">
        <div
          v-for="item in combinedHighlights"
          :key="item.key"
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <p class="mb-1 text-xs uppercase tracking-wide text-n-slate-10">
            {{ item.title }}
          </p>
          <p class="mb-1 text-lg font-semibold text-n-slate-12">
            {{ item.value }}
          </p>
          <p class="mb-0 break-words text-sm text-n-slate-11">
            {{ item.label }}
          </p>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUMMARY') }}
            </p>
            <span class="chatmigo-report-chip">
              {{ analyticsSummary.length }}
            </span>
          </div>

          <div v-if="analyticsSummary.length" class="grid gap-3 sm:grid-cols-2">
            <div
              v-for="metric in analyticsSummary"
              :key="metric.key"
              class="chatmigo-report-metric-card p-3"
            >
              <p class="mb-1 text-xs text-n-slate-10">
                {{ metric.label }}
              </p>
              <p class="mb-0 break-words text-lg font-semibold text-n-slate-12">
                {{ metric.value }}
              </p>
            </div>
          </div>

          <p
            v-else
            class="chatmigo-report-empty-state mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUMMARY_EMPTY')
            }}
          </p>
        </div>

        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CONVERSATIONS')
              }}
            </p>
            <span class="chatmigo-report-chip">
              {{ conversationSummary.length }}
            </span>
          </div>

          <div
            v-if="conversationSummary.length"
            class="grid gap-3 sm:grid-cols-2"
          >
            <div
              v-for="metric in conversationSummary"
              :key="metric.key"
              class="chatmigo-report-metric-card p-3"
            >
              <p class="mb-1 text-xs text-n-slate-10">
                {{ metric.label }}
              </p>
              <p class="mb-0 break-words text-lg font-semibold text-n-slate-12">
                {{ metric.value }}
              </p>
            </div>
          </div>

          <p
            v-else
            class="chatmigo-report-empty-state mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUMMARY_EMPTY')
            }}
          </p>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TREND') }}
            </p>
            <span class="chatmigo-report-chip">
              {{ analyticsTimeline.length }}
            </span>
          </div>

          <div v-if="analyticsTimeline.length" class="grid gap-3">
            <div
              v-for="row in analyticsTimeline"
              :key="`analytics-${row.label}`"
              class="chatmigo-report-data-row grid gap-2 px-3 py-3"
            >
              <div
                class="flex items-center justify-between gap-3 text-xs text-n-slate-10"
              >
                <span>{{ row.label }}</span>
                <span>{{ totalRowValue(row) }}</span>
              </div>
              <div class="chatmigo-report-progress-track h-2">
                <div
                  class="h-2 rounded-full bg-n-brand"
                  :style="{
                    width: `${analyticsTimelineMax ? (totalRowValue(row) / analyticsTimelineMax) * 100 : 0}%`,
                  }"
                />
              </div>
            </div>
          </div>

          <p
            v-else
            class="chatmigo-report-empty-state mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TREND_EMPTY') }}
          </p>
        </div>

        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <div class="mb-4 flex items-center justify-between gap-3">
            <p class="mb-0 text-sm font-semibold text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CONVERSATION_TREND'
                )
              }}
            </p>
            <span class="chatmigo-report-chip">
              {{ conversationTimeline.length }}
            </span>
          </div>

          <div v-if="conversationTimeline.length" class="grid gap-3">
            <div
              v-for="row in conversationTimeline"
              :key="`conversation-${row.label}`"
              class="chatmigo-report-data-row grid gap-2 px-3 py-3"
            >
              <div
                class="flex items-center justify-between gap-3 text-xs text-n-slate-10"
              >
                <span>{{ row.label }}</span>
                <span>{{ totalRowValue(row) }}</span>
              </div>
              <div class="chatmigo-report-progress-track h-2">
                <div
                  class="h-2 rounded-full bg-n-green-9"
                  :style="{
                    width: `${conversationTimelineMax ? (totalRowValue(row) / conversationTimelineMax) * 100 : 0}%`,
                  }"
                />
              </div>
            </div>
          </div>

          <p
            v-else
            class="chatmigo-report-empty-state mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TREND_EMPTY') }}
          </p>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <details
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
          open
        >
          <summary class="cursor-pointer text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_RAW') }}
          </summary>
          <pre
            class="chatmigo-report-code-block mt-3 mb-0 overflow-auto p-3 text-xs text-n-slate-11"
          >
            {{ formattedAnalytics }}
          </pre>
        </details>
        <details
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
          open
        >
          <summary class="cursor-pointer text-sm font-semibold text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CONVERSATIONS_RAW'
              )
            }}
          </summary>
          <pre
            class="chatmigo-report-code-block mt-3 mb-0 overflow-auto p-3 text-xs text-n-slate-11"
          >
            {{ formattedConversationAnalytics }}
          </pre>
        </details>
      </div>

      <div class="grid gap-4 lg:grid-cols-2">
        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <p class="mb-4 text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_TABLE') }}
          </p>
          <div v-if="analyticsHighlights.length" class="grid gap-3">
            <div
              v-for="metric in analyticsHighlights"
              :key="metric.key"
              class="chatmigo-report-data-row flex items-center justify-between gap-3 px-3 py-2"
            >
              <span class="break-words text-sm text-n-slate-11">{{
                metric.label
              }}</span>
              <strong class="text-sm text-n-slate-12">{{
                metric.value
              }}</strong>
            </div>
          </div>
          <p
            v-else
            class="mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUMMARY_EMPTY')
            }}
          </p>
        </div>

        <div
          class="chatmigo-report-card rounded-xl border border-n-container bg-n-alpha-2 p-4"
        >
          <p class="mb-4 text-sm font-semibold text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_CONVERSATION_TABLE'
              )
            }}
          </p>
          <div v-if="conversationHighlights.length" class="grid gap-3">
            <div
              v-for="metric in conversationHighlights"
              :key="metric.key"
              class="chatmigo-report-data-row flex items-center justify-between gap-3 px-3 py-2"
            >
              <span class="break-words text-sm text-n-slate-11">{{
                metric.label
              }}</span>
              <strong class="text-sm text-n-slate-12">{{
                metric.value
              }}</strong>
            </div>
          </div>
          <p
            v-else
            class="mb-0 rounded-xl border border-dashed border-n-container bg-n-background px-4 py-6 text-sm text-n-slate-11"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUMMARY_EMPTY')
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
